/**
 * The formatting one marker can carry. Independent flags rather than a union,
 * because a span can be bold and italic and struck at once.
 */
export type InlineMarks = { bold: boolean; italic: boolean; strike: boolean };

export type MarkupTextRun = { kind: 'text'; text: string };

export type MarkupSpanRun = {
  kind: 'span';
  text: string;
  /**
   * 1-based position in the link list, when the marker carried an index. A
   * span can be marked without linking (`{b|word}`) and linked without marks
   * (`{1|word}`), so this is optional and the marks are always present.
   */
  index?: number;
  /**
   * 1-based position in the styling list, when the marker carried a `.index`.
   * Independent of everything else on the span: `{.1|word}` is styled only,
   * `{b2.1|word}` is bold, linked, and styled.
   */
  styleIndex?: number;
  marks: InlineMarks;
};

export type MarkupBreakRun = { kind: 'break' };

export type MarkupRun = MarkupTextRun | MarkupSpanRun | MarkupBreakRun;

/**
 * `{` + optional mark letters + optional link index + optional `.` and styling
 * index + `|` + text + `}`.
 *
 * Sticky, so it can be tried at one position without slicing the string. The
 * text is everything up to the first `}`, which is what makes markers
 * non-nesting.
 *
 * The dot is what tells the two indexes apart, and it reads as the CSS class
 * selector it usually stands for. It cannot be confused with a link index,
 * because a link index is bare digits and always comes first.
 */
const MARKER = /\{([a-z]*)(\d*)(?:\.(\d+))?\|([^}]*)\}/y;

/**
 * One letter per mark. Order is not significant (`{bi1|x}` and `{ib1|x}` are
 * the same span) and repeats are harmless, because the letters resolve to a
 * set.
 *
 * **`r` must never become a mark letter.** `br` is the line-break token below,
 * and it is only unambiguous while `r` cannot appear in a mark sequence.
 */
const MARK_LETTERS: Readonly<Record<string, keyof InlineMarks>> = {
  b: 'bold',
  i: 'italic',
  s: 'strike',
};

/** The one marker that carries no text: `{br|}`. */
const BREAK_TOKEN = 'br';

const NO_MARKS: InlineMarks = { bold: false, italic: false, strike: false };

/**
 * Letters to a mark set, or `undefined` when any letter is not a mark. An
 * unknown letter makes the whole marker literal text rather than a span with
 * the bad letter ignored: an author who mistypes should see it.
 */
const resolveMarks = (letters: string): InlineMarks | undefined => {
  if (letters === '') {
    return { ...NO_MARKS };
  }

  const marks: InlineMarks = { ...NO_MARKS };
  for (const letter of letters) {
    const mark = MARK_LETTERS[letter];
    if (mark === undefined) {
      return undefined;
    }
    marks[mark] = true;
  }
  return marks;
};

/**
 * Splits authored text into plain runs, marked/linked spans, and line breaks.
 *
 * The grammar, and everything it deliberately leaves as literal text, is
 * specified in `rulecms/__docs__/__ARCHITECTURE__/INLINE_MARKUP_ARCHITECTURE.md`.
 * In short: a marker is `{`, then lowercase mark letters, then a link index,
 * then `.` and a styling index, then `|`, then its text, then `}`. At least one
 * of those three must be present, and everything that does not match that shape
 * stays verbatim — `{name}`, `{99}`, a lone `{`, JSON fragments, an unterminated
 * marker, an unknown mark letter, and empty span text.
 *
 * Indexes are 1-based and normalize through a base-10 parse, so `{01|x}` and
 * `{1|x}` point at the same entry. Resolving an index to an entry is the
 * caller's job; this stays pure and free of React.
 */
export function parseInlineMarkers(value: string): MarkupRun[] {
  if (typeof value !== 'string' || value === '') {
    return [];
  }

  const runs: MarkupRun[] = [];
  let pendingText = '';
  let cursor = 0;

  const flushPendingText = () => {
    if (pendingText !== '') {
      runs.push({ kind: 'text', text: pendingText });
      pendingText = '';
    }
  };

  while (cursor < value.length) {
    const open = value.indexOf('{', cursor);
    if (open === -1) {
      pendingText += value.slice(cursor);
      break;
    }

    MARKER.lastIndex = open;
    const match = MARKER.exec(value);
    const run = match ? resolveRun(match[1], match[2], match[3], match[4]) : undefined;

    if (run === undefined) {
      // Not a marker: keep the brace as text and look for the next one.
      pendingText += value.slice(cursor, open + 1);
      cursor = open + 1;
      continue;
    }

    pendingText += value.slice(cursor, open);
    flushPendingText();
    runs.push(run);
    cursor = open + (match as RegExpExecArray)[0].length;
  }

  flushPendingText();

  return runs;
}

/**
 * One matched `{…|…}` to the run it means, or `undefined` when the match is
 * not a marker after all and its opening brace is literal text.
 *
 * `styleDigits` is `undefined` when the marker carried no dot at all, which is
 * different from a dot with nothing after it — the regex does not match that,
 * so it never reaches here.
 */
const resolveRun = (
  letters: string,
  digits: string,
  styleDigits: string | undefined,
  text: string
): MarkupRun | undefined => {
  if (letters === BREAK_TOKEN) {
    // A break carries nothing. `{br|x}` is a mistake, not a break with text.
    return digits === '' && styleDigits === undefined && text === ''
      ? { kind: 'break' }
      : undefined;
  }

  if (letters === '' && digits === '' && styleDigits === undefined) {
    return undefined;
  }

  // Empty text has no words to mark or link, and staying literal is what shows
  // the author the mistake instead of swallowing the marker.
  if (text === '') {
    return undefined;
  }

  const marks = resolveMarks(letters);
  if (marks === undefined) {
    return undefined;
  }

  return {
    kind: 'span',
    text,
    ...(digits === '' ? {} : { index: Number.parseInt(digits, 10) }),
    ...(styleDigits === undefined ? {} : { styleIndex: Number.parseInt(styleDigits, 10) }),
    marks,
  };
};
